/*************************************************
* ByzFunder
 * @exports
 * index.tsx
 * Created by vinoth kumar on 08/05/2024
 * Copyright © 2024 ByzFunder. All rights reserved.
 *************************************************/
// Import necessary modules
import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Loading from './../components/pages/common/Loading';
import store from "./../redux/SessionStorage";
import { NavigationHandler } from "./NavigationHandler";

// Define a reusable Suspense wrapper component
const Loadable = (Component: any) => (props: any) => (
	<Suspense fallback={<Loading />}>
		<Component {...props} />
	</Suspense>
);

// Define your route components lazily
const Login = Loadable(lazy(() => import("../components/pages/Login")));
const ForgotPassword = Loadable(lazy(() => import("../components/pages/ForgotPassword")));
const ResetPassword = Loadable(lazy(() => import("../components/pages/ResetPassword")));
const Signup = Loadable(lazy(() => import("../components/pages/Signup")));
const SetPassword = Loadable(lazy(() => import("../components/pages/SetPassword")));
const OtpVerification = Loadable(lazy(() => import("../components/pages/OtpVerification")));
const Dashboard = Loadable(lazy(() => import("../components/pages/Dashboard")));
const ErrorPage = Loadable(lazy(() => import("../components/pages/ErrorPage")));
// Online MCA Checkout Components
const ViewOffer = Loadable(lazy(() => import("../components/online-checkout/mca/ViewOffer")));
const IdentityVerification = Loadable(lazy(() => import("../components/online-checkout/mca/IdentityVerification")));
const SignContract = Loadable(lazy(() => import("../components/online-checkout/mca/SignContract")));
const BankConfirmation = Loadable(lazy(() => import("../components/online-checkout/mca/BankConfirmation")));
const BankVerification = Loadable(lazy(() => import("../components/online-checkout/mca/BankVerification")));
const Stips = Loadable(lazy(() => import("../components/online-checkout/mca/Stips")));
const ReviewCheckout = Loadable(lazy(() => import("../components/online-checkout/mca/ReviewCheckout")));
const SuccessCheckout = Loadable(lazy(() => import("../components/online-checkout/mca/SuccessCheckout")));
const BankSelectConfirmation = Loadable(lazy(() => import("../components/online-checkout/mca/BankSelectConfirmation")));
const PendingDashboard = Loadable(lazy(() => import("../components/online-checkout/mca/PendingDashboard")));
// Online ByzFlex Checkout Components
const ByzFlexViewOffer = Loadable(lazy(() => import("../components/online-checkout/byzflex/ViewOffer")));
const ByzFlexBankConfirmation = Loadable(lazy(() => import("../components/online-checkout/byzflex/BankConfirmation")));
const ByzFlexBankSelectConfirmation = Loadable(lazy(() => import("../components/online-checkout/byzflex/BankSelectConfirmation")));
const ByzFlexBankVerification = Loadable(lazy(() => import("../components/online-checkout/byzflex/BankVerification")));
const ByzFlexIdentityVerification = Loadable(lazy(() => import("../components/online-checkout/byzflex/IdentityVerification")));
const ByzFlexSignContract = Loadable(lazy(() => import("../components/online-checkout/byzflex/SignContract")));
const ByzFlexReviewCheckout = Loadable(lazy(() => import("../components/online-checkout/byzflex/ReviewCheckout")));
const ByzFlexSuccessCheckout = Loadable(lazy(() => import("../components/online-checkout/byzflex/SuccessCheckout")));
const ByzFlexPendingDashboard = Loadable(lazy(() => import("../components/online-checkout/byzflex/PendingDashboard")));
const SocureSuccess = Loadable(lazy(() => import("../components/online-checkout/SocureSuccess")));
const ByzFlexStips = Loadable(lazy(() => import("../components/online-checkout/byzflex/Stips")));
// ENG-4369: Secondary principal (co-owner) checkout — no PrivateRoute (arrives via email link)
const SecondaryViewOffer = Loadable(lazy(() => import("../components/online-checkout/secondary/SecondaryViewOffer")));
const SecondaryIdentityVerification = Loadable(lazy(() => import("../components/online-checkout/secondary/SecondaryIdentityVerification")));
const SecondarySignContract = Loadable(lazy(() => import("../components/online-checkout/secondary/SecondarySignContract")));
const SecondaryComplete = Loadable(lazy(() => import("../components/online-checkout/secondary/SecondaryComplete")));
const PrimaryAwaitingSecondary = Loadable(lazy(() => import("../components/online-checkout/secondary/PrimaryAwaitingSecondary")));

const PrivateRoute = ({ children }: any) => {
	const user = store.get("user")
		? JSON.parse(store.get("user") || "")
		: null;
	if (!user) {
		return <Navigate to="/login" />;
	}
	return children;
};
// NavigationHandler must stay the imported, server-authoritative one. An earlier local copy of the
// same name shadowed that module and resolved redirects from localStorage alone, which let a stale
// cached step outrank the server — a merchant whose steps were deleted in the Online Checkout Log
// got redirected back into a step that no longer exists (ENG-3810 / ENG-5096).

// Define your route configuration within the context of Router
export default function AppRouter() {
	return (
		<Router>
			<NavigationHandler />
			<Routes>
				<Route path="/login" element={<Login />} />
				<Route path="/forgot-password" element={<ForgotPassword />} />
				<Route path="/reset-password" element={<ResetPassword />} />
				<Route path="/signup" element={<Signup />} />
				<Route path="/set-password" element={<SetPassword />} />
				<Route path="/otp-verify" element={<OtpVerification />} />
				<Route path="/error" element={<ErrorPage />} />
				<Route path="/dashboard"
					element={
						<PrivateRoute>
							<Dashboard />
						</PrivateRoute>
					} />
				<Route path="/" element={<Navigate to="/login" />} />
				<Route path="*" element={<Navigate to="/login" />} />
				{/* Online MCA Checkout Routes */}
				<Route path="/online-checkout/view-offer" element={<PrivateRoute><ViewOffer /></PrivateRoute>} />
				<Route path="/online-checkout/identity-verification" element={<PrivateRoute><IdentityVerification /></PrivateRoute>} />
				<Route path="/online-checkout/sign-contract" element={<PrivateRoute><SignContract /></PrivateRoute>} />
				<Route path="/online-checkout/bank-verification" element={<PrivateRoute><BankVerification /></PrivateRoute>} />
				<Route path="/online-checkout/bank-confirmation" element={<PrivateRoute><BankConfirmation /></PrivateRoute>} />
				<Route path="/online-checkout/stips" element={<PrivateRoute><Stips /></PrivateRoute>} />
				<Route path="/online-checkout/review-checkout" element={<PrivateRoute><ReviewCheckout /></PrivateRoute>} />
				<Route path="/online-checkout/success-checkout" element={<PrivateRoute><SuccessCheckout /></PrivateRoute>} />
				<Route path="/online-checkout/bank-select-confirmation" element={<PrivateRoute><BankSelectConfirmation /></PrivateRoute>} />
				<Route path="/online-checkout/pending-dashboard" element={<PrivateRoute><PendingDashboard /></PrivateRoute>} />
				{/* Online ByzFlex Checkout Routes */}
				<Route path="/online-checkout/byzflex/view-offer" element={<PrivateRoute><ByzFlexViewOffer /></PrivateRoute>} />
				<Route path="/online-checkout/byzflex/bank-confirmation" element={<PrivateRoute><ByzFlexBankConfirmation /></PrivateRoute>} />
				<Route path="/online-checkout/byzflex/bank-verification" element={<PrivateRoute><ByzFlexBankVerification /></PrivateRoute>} />
				<Route path="/online-checkout/byzflex/bank-select-confirmation" element={<PrivateRoute><ByzFlexBankSelectConfirmation /></PrivateRoute>} />
				<Route path="/online-checkout/byzflex/identity-verification" element={<PrivateRoute><ByzFlexIdentityVerification /></PrivateRoute>} />
				<Route path="/online-checkout/byzflex/sign-contract" element={<PrivateRoute><ByzFlexSignContract /></PrivateRoute>} />
				<Route path="/online-checkout/byzflex/stips" element={<PrivateRoute><ByzFlexStips /></PrivateRoute>} />
				<Route path="/online-checkout/byzflex/review-checkout" element={<PrivateRoute><ByzFlexReviewCheckout /></PrivateRoute>} />
				<Route path="/online-checkout/byzflex/success-checkout" element={<PrivateRoute><ByzFlexSuccessCheckout /></PrivateRoute>} />
				<Route path="/online-checkout/byzflex/pending-dashboard" element={<PrivateRoute><ByzFlexPendingDashboard /></PrivateRoute>} />
				<Route path="/online-checkout/socure-success" element={<SocureSuccess />} />
				{/* ENG-4369: Secondary principal (co-owner) checkout */}
				<Route path="/secondary-checkout/view-offer" element={<PrivateRoute><SecondaryViewOffer /></PrivateRoute>} />
				<Route path="/secondary-checkout/identity-verification" element={<PrivateRoute><SecondaryIdentityVerification /></PrivateRoute>} />
								<Route path="/secondary-checkout/sign-contract" element={<PrivateRoute><SecondarySignContract /></PrivateRoute>} />
				<Route path="/secondary-checkout/complete" element={<PrivateRoute><SecondaryComplete /></PrivateRoute>} />
				{/* ENG-4356 / ENG-4373: Primary signer awaiting secondary — shown after notify */}
				<Route path="/online-checkout/primary-awaiting" element={<PrivateRoute><PrimaryAwaitingSecondary /></PrivateRoute>} />
			</Routes>
		</Router>
	);
}